/* eslint-disable */
import type { Money } from "../../../common/currency.pb";
import type { BitcoinAmount } from "../../crypto/amount.pb";

/** Status of requests issue to the lightning network */
export enum LightningRequestStatus {
  /** UNKNOWN - Default is undefined and indicates a versioning error. */
  UNKNOWN = "UNKNOWN",
  PENDING = "PENDING",
  SUCCESS = "SUCCESS",
  FAILURE = "FAILURE",
  ERROR = "ERROR",
  TIMEOUT = "TIMEOUT",
}

export enum EventType {
  /** EVENT_TYPE_UNKNOWN - Default is undefined and indicates a versioning error. */
  EVENT_TYPE_UNKNOWN = "EVENT_TYPE_UNKNOWN",
  /**
   * PAYMENT_INITIATED - The payment has been initiated.
   * This event does not represent a transition, it is only emitted on creation.
   */
  PAYMENT_INITIATED = "PAYMENT_INITIATED",
  /**
   * PAYMENT_TRANSFERRED - The payment has been successfully processed to completion.
   * Represents a transition from SUBMITTED to COMPLETE
   */
  PAYMENT_TRANSFERRED = "PAYMENT_TRANSFERRED",
  /**
   * PAYMENT_LIMITED - The payment could not be processed due to the initiator's transfer limit being reached.
   * Represents a transition from NEW to LIMITED
   */
  PAYMENT_LIMITED = "PAYMENT_LIMITED",
  /**
   * PAYMENT_SUBMITTED - The payment has been submitted and is confirmation from Lit.
   * Represents a transition from RESERVED to SUBMITTED
   */
  PAYMENT_SUBMITTED = "PAYMENT_SUBMITTED",
  /**
   * PAYMENT_RISK_BLOCKED - The payment has failed risk evaluation and has been blocked accordingly.
   * Represents a transition to RISK_BLOCKED.
   */
  PAYMENT_RISK_BLOCKED = "PAYMENT_RISK_BLOCKED",
  /**
   * PAYMENT_ORPHANED - The payment was abandoned whilst still in the NEW state, indicating the customer started
   * another new payment
   * Represents a transition from NEW to ORPHANED.
   */
  PAYMENT_ORPHANED = "PAYMENT_ORPHANED",
  /**
   * PAYMENT_FAILED - The payment has failed while initiating, usually because of a Plasma failure.
   * Represents a transition from NEW, SUBMITTED or RESERVED to FAILED
   */
  PAYMENT_FAILED = "PAYMENT_FAILED",
  /**
   * PAYMENT_RESERVED - The payment has been reserved in Banklin
   * Represents a transition from VALID to RESERVED
   */
  PAYMENT_RESERVED = "PAYMENT_RESERVED",
  /**
   * PAYMENT_VALID - The payment has passed the validation rules and a route was found on the lightning network
   * Represents a transition from NEW to VALID
   */
  PAYMENT_VALID = "PAYMENT_VALID",
  /**
   * PAYMENT_PROBED - The payment has passed the validation rules and a route was found on the lightning network
   * This state is no longer used.
   */
  PAYMENT_PROBED = "PAYMENT_PROBED",
  /**
   * PAYMENT_VOIDED - The payment has been voided in Banklin.
   * Represents a transition from SUBMITTED to VOIDED
   */
  PAYMENT_VOIDED = "PAYMENT_VOIDED",
}

/** A crypto-sparky view of a lightning withdrawal. */
export interface LightningWithdrawal {
  /** The lightning withdrawal's unique token */
  token?: string;
  /** The time in epoch millis that this withdrawal was created */
  created_at?: number;
  /** The time in epoch millis that this withdrawal was updated */
  updated_at?: number;
  /** Each time the withdrawal's state is updated, the version increments. Zero-based. */
  version?: number;
  /** The amount being withdrawn. */
  amount?: BitcoinAmount;
  /** The state of this withdrawal, in terms of the crypto-sparky state machine */
  state?: string;
  /** The usd equivalent at time of creation */
  usd_equivalent?: Money;
  /** The fiat equivalent in the customer's local currency */
  fiat_equivalent?: Money;
  /** The source stored balance token from where the funds are withdrawn */
  source_balance_token?: string;
  /** The transaction token generated by Banklin */
  transaction_token?: string;
  /** The result of issuing a probe request to the lightning network */
  probe_result?: LightningRequestStatus;
  /** The result of issuing a send request to the lightning network */
  send_result?: LightningRequestStatus;
  /** The payment hash from the lightning invoice */
  payment_hash?: string;
  /** The public key (or identifier) of the receiving node */
  destination_node_public_key?: string;
  /** The time in epoch milliseconds when this invoice will expire */
  expire_timestamp?: number;
  /** The duration that the invoice is valid for */
  expire_duration?: number;
  /** The customer token */
  customer_token?: string;
  /** The reason attributed to the failure of this withdrawal, if applicable */
  failure_reason?: string;
  /** The reason Lit failed the payment, if applicable */
  lit_failure_reason?: string;
  /** The lightning network invoice the customer is paying. */
  invoice?: string;
}
